import React, { Suspense, useEffect, useState } from 'react';
import { useCurrentLocation } from '../hooks/useCurrentLocation';
import { useAppSwitcherData } from '../hooks/useAppSwitcherData';
import { useUserPreferences } from '../hooks/useUserPreferences';
import { ZDS_BUILDER_HEADER } from '../constants/zds';
import {
  isMainWrapperWithoutPadding,
  isNavigationPartiallyHidden,
  isNavigationPartiallyPlusFooterHidden,
} from '../utils';
import { HistoryObserver } from 'browser-history-observer';
import { userPilotReload } from '../utils/userpilotFunctions';
import {
  NavigationContextProvider,
  NavigationOverrides,
} from '../NavigationContext';
import { AiPanelLayoutProvider } from '../Concierge/AiPanelLayoutContext';
import { AiShellSection } from '../layout/AiShellSection';
import { GlobalNav } from './GlobalNav';
import { MainWrapperComponent } from './MainWrapper';

const LazyEnvironmentBar = React.lazy(() => import('../EnvironmentBarNew'));

const observer = new HistoryObserver();

interface GlobalNavRootProps {
  overrides?: NavigationOverrides;
  hideEnvironmentBar?: boolean;
  hideAiAgent?: boolean;
  suppressBillingEnvironmentBanner?: boolean;
  /** False while the Global Nav flag is still a guess. */
  isGlobalNavConfirmed?: boolean;
}

export const GlobalNavRoot = ({
  overrides = {},
  hideEnvironmentBar = false,
  hideAiAgent = false,
  suppressBillingEnvironmentBanner = false,
  isGlobalNavConfirmed = false,
}: GlobalNavRootProps) => {
  const { href } = useCurrentLocation();
  const { isEnabled: isZASTokenEnabled } = useAppSwitcherData();
  // User preferences are fetched here (not inside GlobalNav) so QueryClient
  // stays at the shell root and the New Zuora Experience flag can flow into
  // nav transform timing (ZPY-458).
  const [userPreferences] = useUserPreferences({ enabled: true });
  const [zNavPartiallyHidden, setZNavPartiallyHidden] = useState(() =>
    isNavigationPartiallyHidden(href),
  );
  const [zNavPartiallyPlusFooterHidden, setZNavPartiallyPlusFooterHidden] =
    useState(
      () =>
        isNavigationPartiallyPlusFooterHidden(href) ||
        !!document.getElementById(ZDS_BUILDER_HEADER),
    );
  const [zNavMainWrapperWithoutPadding, setZNavMainWrapperWithoutPadding] =
    useState(() => isMainWrapperWithoutPadding(href));

  const [, rerenderOnHistoryChange] = useState(0);

  useEffect(() => {
    const unsubscribe = observer.onHistoryChange((payload) => {
      if (payload?.type === 'load') {
        return;
      }
      userPilotReload();
      rerenderOnHistoryChange((prevValue) => prevValue + 1);
      if (payload?.url) {
        setZNavPartiallyHidden(isNavigationPartiallyHidden(payload?.url));
        setZNavPartiallyPlusFooterHidden(
          isNavigationPartiallyPlusFooterHidden(payload?.url),
        );
        setZNavMainWrapperWithoutPadding(
          isMainWrapperWithoutPadding(payload?.url),
        );
      }
    });
    return unsubscribe;
  }, []);

  const isPartialOrPartialPlus =
    zNavPartiallyHidden || zNavPartiallyPlusFooterHidden || hideEnvironmentBar;

  const mainWrapperNode = (
    <MainWrapperComponent
      isPartialOrPartialPlus={isPartialOrPartialPlus}
      isMainWrapperWithoutPadding={zNavMainWrapperWithoutPadding}
      isZASTokenEnabled={isZASTokenEnabled}
    />
  );

  return (
    <NavigationContextProvider
      overrides={overrides}
      suppressBillingEnvironmentBanner={suppressBillingEnvironmentBanner}
    >
      <AiPanelLayoutProvider>
        {!hideEnvironmentBar && (
          <Suspense fallback={<React.Fragment />}>
            <LazyEnvironmentBar />
          </Suspense>
        )}
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              alignItems: 'stretch',
            }}
          >
            <GlobalNav
              isPartialOrPartialPlus={isPartialOrPartialPlus}
              isNewZuoraExperienceEnabled={userPreferences?.reinvented}
              isGlobalNavConfirmed={isGlobalNavConfirmed}
            />
            <AiShellSection
              mainContent={mainWrapperNode}
              hideAiAgent={hideAiAgent}
            />
          </div>
        </div>
      </AiPanelLayoutProvider>
    </NavigationContextProvider>
  );
};
